import React, { useState, useEffect } from 'react';
import { 
  Bell, Lock, Eye, Globe, CreditCard, HelpCircle, 
  ChevronRight, Moon, LogOut, FileText, Building2, Save, Loader2, ShieldCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAuth } from '../context/AuthContext';
// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';
import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const SettingsPage = () => {
  const { logout, user, updateProfile } = useAuth();
  
  // 🔥 Dil Fonksiyonunu Al
  const { t } = useLanguage(); 
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  
  // Form verilerini state'de tut
  const [companyDetails, setCompanyDetails] = useState({
    companyName: '',
    street: '',
    zipCity: '',
    taxId: '',
    iban: '',
    bic: '',
  });

  // Sayfa yüklendiğinde mevcut kullanıcı verilerini forma doldur
  useEffect(() => {
    if (user?.companyDetails) {
      setCompanyDetails({
        companyName: user.companyDetails.companyName || '',
        street: user.companyDetails.street || '',
        zipCity: user.companyDetails.zipCity || '',
        taxId: user.companyDetails.taxId || '',
        iban: user.companyDetails.iban || '',
        bic: user.companyDetails.bic || '',
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      // Backend'e güncelleme isteği gönder
      await updateProfile({ companyDetails });
      
      toast({ 
        title: t('success_general') || "Profile Updated", 
        description: t('settings_saved_desc') || "Your business details have been saved successfully.",
        className: "bg-emerald-500 text-white border-0" 
      });
    } catch (error) {
      toast({ 
        title: t('error_general') || "Error", 
        description: t('settings_error') || "Failed to update profile.", 
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const settingsGroups = [
    {
      title: t('group_general') || 'General',
      items: [
        { icon: Bell, label: t('notifications') || 'Notifications', type: 'switch', value: true },
        { icon: Eye, label: t('setting_dark_mode') || 'Dark Mode', type: 'switch', value: true, disabled: true },
        // Dil değiştirme HeaderActions içinde olduğu için buraya eklemiyoruz, ancak opsiyonel olarak eklenebilir.
      ]
    },
    {
      title: t('security') || 'Security & Privacy',
      items: [
        { icon: Lock, label: t('change_password') || 'Password & Security', type: 'link', path: '/profile' },
        { icon: CreditCard, label: t('subscription') || 'Subscription', type: 'link', path: '/subscription' }
      ]
    },
    {
      title: t('footer_resources') || 'Support',
      items: [
        { icon: HelpCircle, label: t('footer_company') || 'Help Center', type: 'link', path: '/help' },
        { icon: FileText, label: t('footer_impressum') || 'Impressum', type: 'link', path: '/impressum' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1120] text-white font-sans flex flex-col relative overflow-x-hidden">
      <Toaster />
      
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
           <h1 className="text-xl font-black uppercase tracking-tighter text-white italic flex items-center gap-2">
             <ShieldCheck className="text-blue-500" size={24} />
             FOX <span className="text-blue-500 font-normal">{t('settings_title') || 'CONTROL'}</span>
           </h1>
           <HeaderActions />
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 py-10 relative z-10 space-y-12">
        
        {/* --- 1. BUSINESS PROFILE (CRITICAL FOR INVOICES) --- */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-between px-2">
            <div>
              <h2 className="text-lg font-black uppercase tracking-widest text-white italic">{t('firm_data') || 'Business Profile'}</h2>
              <p className="text-[10px] text-slate-500 font-bold uppercase mt-1">{t('business_profile_desc') || 'Used for DIN-5008 Documents'}</p>
            </div>
            <Button 
              onClick={handleSaveCompany} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-xs font-black uppercase tracking-widest rounded-xl h-10 px-6 shadow-lg shadow-blue-900/20 active:scale-95 transition-all"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <span className="flex items-center gap-2"><Save size={14} /> {t('action_save') || 'Save'}</span>}
            </Button>
          </div>

          <div className="glass-panel p-8 rounded-[2.5rem] border border-white/5 bg-white/[0.02] grid grid-cols-1 md:grid-cols-2 gap-6 shadow-2xl">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('label_fullname') || 'Company Name'}</label>
              <Input name="companyName" value={companyDetails.companyName} onChange={handleInputChange} placeholder="Max Mustermann GmbH" className="h-12 bg-black/20 border-white/10 rounded-xl focus:border-blue-500/50 transition-colors" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('label_tax_id') || 'Tax ID'}</label>
              <Input name="taxId" value={companyDetails.taxId} onChange={handleInputChange} placeholder="12/345/67890" className="h-12 bg-black/20 border-white/10 rounded-xl font-mono text-slate-300" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('address') || 'Street & Nr.'}</label>
              <Input name="street" value={companyDetails.street} onChange={handleInputChange} placeholder="Musterstraße 1" className="h-12 bg-black/20 border-white/10 rounded-xl" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('label_zip_city') || 'ZIP & City'}</label>
              <Input name="zipCity" value={companyDetails.zipCity} onChange={handleInputChange} placeholder="10115 Berlin" className="h-12 bg-black/20 border-white/10 rounded-xl" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">{t('iban_placeholder') || 'IBAN'}</label>
              <Input name="iban" value={companyDetails.iban} onChange={handleInputChange} placeholder="DE00 0000 0000..." className="h-12 bg-black/20 border-white/10 rounded-xl font-mono text-blue-400" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">BIC</label>
              <Input name="bic" value={companyDetails.bic} onChange={handleInputChange} placeholder="GENO DE FF..." className="h-12 bg-black/20 border-white/10 rounded-xl font-mono" />
            </div>
          </div>
        </motion.section>

        {/* --- 2. REGULAR SETTINGS GROUPS --- */}
        <div className="space-y-8">
            {settingsGroups.map((group, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * (idx + 1), duration: 0.5 }}
                  className="space-y-4"
                >
                    <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] ml-4">{group.title}</h3>
                    <div className="bg-[#111827]/50 backdrop-blur-sm border border-white/5 rounded-[2rem] overflow-hidden shadow-lg hover:border-white/10 transition-colors">
                        {group.items.map((item, i) => (
                            <div 
                                key={i} 
                                onClick={item.type === 'link' && item.path ? () => navigate(item.path) : item.action}
                                className={`flex items-center justify-between p-5 hover:bg-white/5 transition-all group ${item.type === 'link' ? 'cursor-pointer' : ''} ${i !== group.items.length - 1 ? 'border-b border-white/5' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="p-2.5 bg-white/5 rounded-2xl text-slate-400 group-hover:text-blue-400 group-hover:bg-blue-500/10 transition-colors">
                                        <item.icon size={18} />
                                    </div>
                                    <span className="font-bold text-sm text-slate-200 group-hover:text-white transition-colors">{item.label}</span>
                                </div>
                                
                                {item.type === 'switch' && (
                                    <Switch checked={item.value} disabled={item.disabled} />
                                )}
                                
                                {item.type === 'link' && (
                                    <ChevronRight size={16} className="text-slate-600 group-hover:text-white transition-colors group-hover:translate-x-1" />
                                )}
                            </div>
                        ))}
                    </div>
                </motion.div>
            ))}

            <motion.button 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={logout}
              className="w-full bg-red-500/5 hover:bg-red-500/10 text-red-500 p-6 rounded-[2rem] font-black uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3 transition-all border border-red-500/10 mt-12 active:scale-[0.98]"
            >
                <LogOut size={16} /> {t('menu_logout') || 'Log Out'}
            </motion.button>
        </div>

      </main>

      <Footer />
    </div>
  );
};

export default SettingsPage;